import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from 'lucide-react'

interface PDFControlsProps {
  currentPage: number
  totalPages: number
  zoom: number
  onFirstPage: () => void
  onPreviousPage: () => void
  onNextPage: () => void
  onLastPage: () => void
  onGoToPage: (page: number) => void
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
}

export default function PDFControls({
  currentPage,
  totalPages,
  zoom,
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
  onGoToPage,
  onZoomIn,
  onZoomOut,
  onResetZoom
}: PDFControlsProps) {
  const { t } = useTranslation()
  const [pageInput, setPageInput] = useState(currentPage.toString())

  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPages

  // Sync input with currentPage when it changes externally
  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value)
  }

  const navigateToInputPage = () => {
    const pageNum = parseInt(pageInput, 10)
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      onGoToPage(pageNum)
    } else {
      // Reset to current page if invalid
      setPageInput(currentPage.toString())
    }
  }

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      navigateToInputPage()
      e.currentTarget.blur()
    }
  }

  const handlePageInputBlur = () => {
    navigateToInputPage()
  }

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 bg-base-100 border-b border-base-300">
      {/* Page Navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={onFirstPage}
          disabled={isFirstPage}
          className="btn btn-sm btn-ghost btn-circle"
          title={t('pdfViewer.controls.firstPage')}
        >
          <ChevronFirst className="w-4 h-4" />
        </button>
        <button
          onClick={onPreviousPage}
          disabled={isFirstPage}
          className="btn btn-sm btn-ghost btn-circle"
          title={t('pdfViewer.controls.previousPage')}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-1 px-3 text-sm font-medium text-base-content/70">
          <input
            type="text"
            value={pageInput}
            onChange={handlePageInputChange}
            onKeyDown={handlePageInputKeyDown}
            onBlur={handlePageInputBlur}
            className="input input-sm w-12 h-7 px-2 text-center bg-base-200 border-base-300 focus:outline-none focus:border-primary"
          />
          <span>of {totalPages}</span>
        </div>

        <button
          onClick={onNextPage}
          disabled={isLastPage}
          className="btn btn-sm btn-ghost btn-circle"
          title={t('pdfViewer.controls.nextPage')}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={onLastPage}
          disabled={isLastPage}
          className="btn btn-sm btn-ghost btn-circle"
          title={t('pdfViewer.controls.lastPage')}
        >
          <ChevronLast className="w-4 h-4" />
        </button>
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={onZoomOut}
          disabled={zoom <= 25}
          className="btn btn-sm btn-ghost btn-circle"
          title={t('pdfViewer.controls.zoomOut')}
        >
          <ZoomOut className="w-4 h-4" />
        </button>

        <div className="px-3 text-sm font-medium text-base-content/70 min-w-[60px] text-center">
          {Math.round(zoom)}%
        </div>

        <button
          onClick={onZoomIn}
          disabled={zoom >= 200}
          className="btn btn-sm btn-ghost btn-circle"
          title={t('pdfViewer.controls.zoomIn')}
        >
          <ZoomIn className="w-4 h-4" />
        </button>

        <button
          onClick={onResetZoom}
          className="btn btn-sm btn-ghost btn-circle"
          title={t('pdfViewer.controls.resetZoom')}
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
